import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'
import ModalContext from 'contexts/ModalContext'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import WaitingListRequestFormModal from '../WaitingListRequest/WaitingListRequestFormModal'
import PrivateRequestFormModal from '../PrivateRequest/PrivateRequestFormModal'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import type { DepartureSoldOutReason } from 'lib/tours/tourDepartureUtils'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import { isTourLimitedTimeOffer } from 'lib/tours/tourUtils'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

const Tile = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: grid;
  padding: ${rem(20)};
  gap: ${rem(16)};
  grid-template-areas:
  "dates"
  "call"
  "cta";


  ${mediaQueryUp.tablet} {
    column-gap: ${rem(40)};
    row-gap: ${rem(12)};
    padding: ${rem(16)} 0;
    grid-template-columns: 1fr min-content;
    grid-template-areas:
    "dates cta"
    "call cta";
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(500)} ${rem(210)} 1fr;
    grid-template-areas: "dates call cta";
  }
`

interface Props {
  offer: App.Tours.TourV2Offer
  travellers: Array<OfferPageRoom>
  reason: DepartureSoldOutReason
  onUpdateTravelers?: () => void
}

function TourV2DeparturesSoldOutTile(props: Props) {
  const {
    offer,
    travellers,
    reason,
    onUpdateTravelers,
  } = props

  const intl = useIntl()
  const contact = useContext(ContactContext)
  const showModal = useContext(ModalContext)

  const onJoinWaitlist = useCallback(() => {
    showModal(<WaitingListRequestFormModal offer={offer} />)
  }, [offer, showModal])

  const onRequestPrivateDeparture = useCallback(() => {
    showModal(<PrivateRequestFormModal
      offer={offer}
      defaultTravellers={travellers}
    />)
  }, [travellers, offer, showModal])

  return <Tile>
    <div
      style={{ gridArea: 'dates' }}
    >
      <Heading variant="heading4">{intl.formatMessage({
        id: '4dzt4pecDO',
        defaultMessage: 'No departures available',
        description: 'Heading when no tour departures are available',
      })}
      </Heading>
      <BodyText variant="medium">
        {/* TODO: i18n - contains leading/trailing spaces */}
        {reason === 'travellers' && <>
          There are no departures matching your traveller selection of {formatOccupantsShort(travellers)}
        </>}
        {reason === 'sold-out' && <>{intl.formatMessage({
          id: 'g+ZWzmByM0',
          defaultMessage: 'Sorry, this offer has sold out!',
          description: 'Message when tour offer is completely sold out',
        })}
        </>}
      </BodyText>
    </div>
    <div style={{ gridArea: 'call' }}>
      {/* TODO: i18n - contains leading/trailing spaces */}
      <BodyText variant="medium">
        Give our team a call on{' '}
        <TextLink wrap="no-wrap" href={`tel:${contact.defaultContact.number}`}>
          {contact.defaultContact.humanReadable}
        </TextLink>{' '}
        for more tour options
        {reason === 'travellers' && <>
          {/* TODO: i18n - contains leading/trailing spaces */}
          {' '}or <TextLink onClick={onUpdateTravelers}>{intl.formatMessage({
            id: 'kGplkaqNH/',
            defaultMessage: 'Update travellers',
            description: 'Link text to update traveller selection',
          })}
          </TextLink>
        </>}
      </BodyText>
    </div>
    <Group
      direction="horizontal"
      gap={8}
      tabletHorizontalAlign="end"
      horizontalAlign="stretch"
      verticalAlign="center"
      style={{ gridArea: 'cta' }}
    >
      {isSalesforceTourV2(offer) && !isTourLimitedTimeOffer(offer) && <TextButton kind="tertiary" onClick={onRequestPrivateDeparture}>{intl.formatMessage({
        id: 'WqjYSTxnzE',
        defaultMessage: 'Request a private departure',
        description: 'Button text to request a private tour departure',
      })}
      </TextButton>}
      <TextButton kind="secondary" onClick={onJoinWaitlist}>{intl.formatMessage({
        id: '+y5YNr0nf5',
        defaultMessage: 'Join waitlist',
        description: 'Button text to join tour waitlist',
      })}
      </TextButton>
    </Group>
  </Tile>
}

type MappedProps = Pick<Props, 'offer' | 'travellers'>

export default withOfferPageState((state): MappedProps => ({
  offer: state.tourV2Offer!,
  travellers: state.rooms,
}), TourV2DeparturesSoldOutTile)
